//================================================================
//  Component: User Search
//================================================================

//  Purpose: This is an input field that allows you to search all LL Users and select a user

//  Properties:
//    - selectedUser = {A useState, this stores the current selected user, search results and query message}
//    - setSelectedUser = {A useState, this is used to set the selected user state}

//  * REQUIRED *
//  You must have a useReducer in the parent component, with the following properties:
//    - newUserInput = {string, this stores the current selected user email}
//    - newUserGivenName = {string, this stores the current selected user given name}
//    - newUserSurname = {string, this stores the current selected user surname}

//  Example:
//    <UserSearch
//      selectedUser={formData}
//      setSelectedUser={setFormData}
//    ></UserSearch>    

//================================================================


//Libraries
import React, { useReducer, useRef, useEffect } from 'react';

//Contexts

//Components

//Functions
import QueryDocument from '../../Library/QueryDocument';

//Images

export default function UserSearch({
    selectedUser,
    setSelectedUser
}) {

    //------------------------------------------------------
    //  useRef
    //------------------------------------------------------

    // Reference to the dialog content
    const dialogRef = useRef(null);

    //------------------------------------------------------
    //  useReducer
    //------------------------------------------------------

    // Used to store the search data
    const [searchData, setSearchData] = useReducer(
        (state, newState) => ({ ...state, ...newState }),
        {
            'searchResults': '',
            'queryMessage': '',
        }
    );

    //------------------------------------------------------
    //  useState
    //------------------------------------------------------

    // Add a flag to track if a user is selected
    const [userSelected, setUserSelected] = React.useState(false);

    //------------------------------------------------------
    //  useEffect
    //------------------------------------------------------

    // Handles outside click detection
    useEffect(() => {
        const handleClickOutside = (event) => {

            // If the click is outside the dialog
            if (dialogRef.current && !dialogRef.current.contains(event.target)) {

                if (userSelected) {

                    // Hide search results but keep the input if a user is selected
                    setSearchData({ 'searchResults': '', 'queryMessage': '' });

                } else {

                    // Clear input and search data if no user is selected
                    setSearchData({ 'searchResults': '', 'queryMessage': '' });

                    setSelectedUser({
                        'newUserInput': '',
                        'newUserGivenName': '',
                        'newUserSurname': ''
                    });
                }
            }
        };

        // Add event listener
        document.addEventListener('mousedown', handleClickOutside);

        // Clean up the event listener on component unmount or when pane closes
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };

    }, [userSelected, setSelectedUser]);

    //------------------------------------------------------
    //  Functions
    //------------------------------------------------------

    // Function used to handle search
    function handleSearch(value) {

        setSearchData({ 'searchResults': '' });
        setSelectedUser({ 'newUserInput': value });

        // Validate input
        if (value?.length === 0) {
            return setSearchData({ 'queryMessage': 'Please enter an email address.' });

        }

        if (value?.length === 2) {
            return setSearchData({ 'queryMessage': 'Search requires more than two characters.' });

        }

        const query = value?.toLowerCase();

        // Search by characters in email
        return QueryDocument('users', [
            ['searcharray', 'array-contains-any', [query]],
            ['status', '==', 'active'],
            ['usertype', 'in', ['lendleaseuser', 'new', 'contractor']]
        ])
            .then((users) => {

                setSearchData({
                    'searchResults': users,
                    'queryMessage': `Showing ${users?.length} result(s)`
                });

            })
            .catch((error) => {

                console.log(error);
                setSearchData({ 'queryMessage': 'Oops something went wrong!' });

            });

    }

    //------------------------------------------------------
    //  HTML
    //------------------------------------------------------

    return (
        <div className='flex flex-col w-full h-full relative'>

            {/* ------------------------------------------------------ */}
            {/*  Users Search Bar                                      */}
            {/* ------------------------------------------------------ */}

            {/* Search Bar */}
            <div className='flex flex-row items-center'>

                {/* Search Input Field */}
                <input
                    className={
                        searchData?.searchResults?.length > 0 ?
                            'w-full h-[42px] px-[15px] py-[10px] bg-[white] border border-solid border-[#D8D8D8] rounded-tl-[5px] rounded-br-none rounded-tr-[5px] rounded-bl-none'
                            :
                            'w-full h-[42px] px-[15px] py-[10px] bg-[white] border border-solid border-[#D8D8D8] rounded-[5px]'
                    }
                    type='text'
                    placeholder='Search user by email address'
                    value={selectedUser?.newUserInput}
                    onChange={(evt) => handleSearch(evt.target.value)}
                    onPaste={(evt) => handleSearch(evt.clipboardData.getData('Text'))}
                ></input>

            </div>

            {/* Search Results */}
            {
                (
                    <div className='absolute w-full top-11 max-h-[150px] overflow-y-auto bg-white shadow-md rounded-tl-none rounded-br-[5px] rounded-tr-none rounded-bl-[5px]' ref={dialogRef}>

                        {/* No Results */}
                        {
                            (searchData?.searchResults?.length === 0 && searchData?.newUserInput !== '' && searchData?.queryMessage !== '') &&
                            <div className='font-medium text-[14px] px-3 py-[12px]'>
                                No results found.
                            </div>
                        }

                        {/* Results */}
                        {
                            searchData?.searchResults?.length > 0 &&
                            searchData?.searchResults.map((user, index) => (
                                <div
                                    key={index}
                                    className='flex flex-row justify-between px-4 py-[13px] cursor-pointer rounded-sm hover:bg-[#F0F0F0]'
                                    onClick={() => {
                                        setSearchData({ 'searchResults': '', 'queryMessage': '' });
                                        setSelectedUser({
                                            'newUserInput': user?.emailaddress,
                                            'newUserGivenName': user?.givenname,
                                            'newUserSurname': user?.surname
                                        });
                                        setUserSelected(true);
                                    }}
                                >

                                    {/* User Detail */}
                                    <div className='flex flex-col'>
                                        <p className='m-0 font-medium text-[14px]'>{user?.givenname} {user?.surname}</p>
                                        <p className='m-0 text-[14px]'>
                                            {user?.emailaddress}
                                            <br></br>
                                            ({user?.jobtitle})
                                        </p>
                                    </div>

                                </div>
                            ))
                        }

                        {/* Total */}
                        <div className='text-sm font-medium px-4 py-2 border-t border-t-[#D8D8D8]' hidden={searchData?.queryMessage.length === 0}>
                            {searchData?.queryMessage}
                        </div>

                    </div>
                )
            }


        </div>
    )

    //------------------------------------------------------
}
